<template>
  <div
    v-if="!isDismissed"
    role="alert"
    v-bind="$attrs"
    class="multi-alert relative flex min-w-0 items-start rounded-md select-none"
    :class="[sizeStyles.root, colorClass, { 'sm:items-center': orientation === 'horizontal' }]"
  >
    <FontAwesomeIcon
      :icon="['fas', appearance.icon]"
      aria-hidden="true"
      class="shrink-0"
      :class="sizeStyles.icon"
    />

    <div
      class="flex min-w-0 flex-1 flex-col"
      :class="orientation === 'horizontal' ? 'gap-2.5 sm:flex-row sm:items-center' : 'gap-2.5'"
    >
      <div class="min-w-0 flex-1">
        <div v-if="title || $slots.title" class="font-medium" :class="sizeStyles.text">
          <slot name="title">{{ title }}</slot>
        </div>
        <div
          v-if="description || $slots.description || $slots.default"
          class="wrap-anywhere"
          :class="[sizeStyles.text, { 'mt-1': title || $slots.title }]"
        >
          <slot name="description">
            <slot>{{ description }}</slot>
          </slot>
        </div>
      </div>

      <div
        v-if="$slots.actions || $slots.action"
        class="flex max-w-full shrink-0 flex-wrap items-center gap-1.5"
      >
        <slot name="actions" :button-color="appearance.buttonColor">
          <slot name="action" :button-color="appearance.buttonColor" />
        </slot>
      </div>
    </div>

    <span v-if="showTimeRemaining" class="shrink-0 tabular-nums" :class="sizeStyles.text">
      {{ formattedTime }}
    </span>
    <button
      v-if="closable"
      type="button"
      aria-label="Melding sluiten"
      class="flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-md hover:ring hover:ring-current hover:ring-inset focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
      @click.stop="handleClose"
    >
      <FontAwesomeIcon :icon="['fas', 'xmark']" aria-hidden="true" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, type VNode } from "vue"
import { useStorage } from "@vueuse/core"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { AlertTypes } from "@/interfaces/component/alert.interface"

type AlertVariant = "solid" | "outline" | "soft" | "subtle"

const props = withDefaults(
  defineProps<{
    /** The status of alert (success, warning, error, info) */
    type?: AlertTypes
    /** The size of alert (small, medium, large) */
    size?: "small" | "medium" | "large"
    /**
     * Background and border treatment.
     * @deprecated do not use this prop
     * @default "subtle"
     */
    variant?: AlertVariant
    /** Place actions below the content or alongside it on larger screens. */
    orientation?: "vertical" | "horizontal"
    /** The title of alert */
    title?: string
    /** Description text, overridden by the description slot or the default slot. */
    description?: string
    /** Whether the alert is closable */
    closable?: boolean
    /** Time in milliseconds before emitting close. */
    autoCloseMs?: number
    /** Whether to show the time remaining */
    showTimeRemaining?: boolean
    /** Persist manual dismissal under this storage key. */
    storageKey?: string
  }>(),
  {
    type: AlertTypes.NEUTRAL,
    size: "medium",
    variant: "subtle",
    orientation: "vertical",
    showTimeRemaining: false
  }
)

const emit = defineEmits<{ close: [] }>()
defineOptions({ inheritAttrs: false, name: "PAlert" })

defineSlots<{
  /** Description content when no named description slot is provided. */
  default?(): VNode[]
  title?(): VNode[]
  description?(): VNode[]
  actions?(props: { buttonColor: "info" | "success" | "warning" | "error" }): VNode[]
  action?(props: { buttonColor: "info" | "success" | "warning" | "error" }): VNode[]
}>()

const appearances = {
  [AlertTypes.NEUTRAL]: {
    icon: "circle-info",
    buttonColor: "info",
    text: "text-info dark:text-info-dark",
    solid: "bg-info text-on-info dark:bg-info-dark dark:text-on-info-dark",
    soft: "bg-info-lighten-2 dark:bg-info-lighten-2-dark",
    ring: "ring ring-inset ring-info-lighten-1 dark:ring-info-lighten-1-dark"
  },
  [AlertTypes.SUCCESS]: {
    icon: "circle-check",
    buttonColor: "success",
    text: "text-success dark:text-success-dark",
    solid: "bg-success text-on-success dark:bg-success-dark dark:text-on-success-dark",
    soft: "bg-success-lighten-2 dark:bg-success-lighten-2-dark",
    ring: "ring ring-inset ring-success-lighten-1 dark:ring-success-lighten-1-dark"
  },
  [AlertTypes.WARNING]: {
    icon: "triangle-exclamation",
    buttonColor: "warning",
    text: "text-warning dark:text-warning-dark",
    solid: "bg-warning text-on-warning dark:bg-warning-dark dark:text-on-warning-dark",
    soft: "bg-warning-lighten-2 dark:bg-warning-lighten-2-dark",
    ring: "ring ring-inset ring-warning-lighten-1 dark:ring-warning-lighten-1-dark"
  },
  [AlertTypes.ERROR]: {
    icon: "circle-xmark",
    buttonColor: "error",
    text: "text-error dark:text-error-dark",
    solid: "bg-error text-on-error dark:bg-error-dark dark:text-on-error-dark",
    soft: "bg-error-lighten-2 dark:bg-error-lighten-2-dark",
    ring: "ring ring-inset ring-error-lighten-1 dark:ring-error-lighten-1-dark"
  }
} as const

const sizes = {
  small: { root: "gap-2 p-2.5", icon: "size-4", text: "text-xs" },
  medium: { root: "gap-2.5 p-4", icon: "size-5", text: "text-sm" },
  large: { root: "gap-3 p-5", icon: "size-6", text: "text-base" }
} as const

const appearance = computed(() => appearances[props.type] ?? appearances[AlertTypes.NEUTRAL])
const sizeStyles = computed(() => sizes[props.size])
const colorClass = computed(() => {
  const { text, solid, soft, ring } = appearance.value
  switch (props.variant) {
    case "solid":
      return solid
    case "outline":
      return [text, ring, "bg-surface dark:bg-surface-dark"]
    case "soft":
      return [text, soft]
    default:
      return [text, soft, ring]
  }
})

const dismissedStorage = props.storageKey ? useStorage(props.storageKey, false) : undefined
const isDismissed = computed(() => dismissedStorage?.value ?? false)
const timeLeft = ref(props.autoCloseMs ?? 0)
const formattedTime = computed(() => Math.ceil(timeLeft.value / 1000))
let closeTimeout: ReturnType<typeof setTimeout> | undefined
let countdownInterval: ReturnType<typeof setInterval> | undefined

function clearTimers() {
  clearTimeout(closeTimeout)
  clearInterval(countdownInterval)
}

function handleClose() {
  clearTimers()
  if (dismissedStorage) dismissedStorage.value = true
  emit("close")
}

onMounted(() => {
  if (isDismissed.value || props.autoCloseMs === undefined) return

  countdownInterval = setInterval(() => {
    timeLeft.value = Math.max(0, timeLeft.value - 1000)
    if (timeLeft.value === 0) clearInterval(countdownInterval)
  }, 1000)

  closeTimeout = setTimeout(() => {
    clearTimers()
    timeLeft.value = 0
    emit("close")
  }, props.autoCloseMs)
})

onUnmounted(clearTimers)
</script>
